import { useEffect } from "react";
import { ff } from "@/lib/feature-flags";

interface SEOHeadProps {
  /** Page title — appended with " | CIEDA" */
  title?: string;
  /** Meta description (max 160 chars) */
  description?: string;
  /** Canonical URL path (e.g. "/panier") */
  path?: string;
  /** Open Graph image URL */
  ogImage?: string;
  /** OG type: website, product, article */
  ogType?: "website" | "product" | "article";
  /** JSON-LD structured data object */
  jsonLd?: Record<string, unknown>;
  /** Prevent indexing (e.g. account pages) */
  noindex?: boolean;
}

const SITE_NAME = "CIEDA";
const SITE_URL = "https://ciedagroup.com";
const DEFAULT_DESCRIPTION =
  "CIEDA — Compagnie Import Export & Distribution Africaine. Produits laitiers, alimentaires et d'entretien au Sénégal et en Afrique de l'Ouest.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Dynamic SEO head management — updates document <head> per page.
 *
 * Uses direct DOM manipulation (no heavy library needed for SPA).
 * Updates: title, meta description, OG tags, canonical, JSON-LD.
 *
 * Controlled by ff.seo feature flag.
 */
export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd,
  noindex = false,
}: SEOHeadProps) {
  useEffect(() => {
    if (!ff.seo) return;

    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Distribution Africaine | Dakar, Sénégal`;
    const canonicalUrl = `${SITE_URL}${path}`;

    // Title
    document.title = fullTitle;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard meta
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "fr_SN");

    // Twitter / WhatsApp
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // JSON-LD structured data
    const jsonLdId = "seo-json-ld";
    let script = document.getElementById(jsonLdId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = jsonLdId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    // Cleanup JSON-LD on unmount
    return () => {
      const el = document.getElementById(jsonLdId);
      if (el) el.remove();
    };
  }, [title, description, path, ogImage, ogType, jsonLd, noindex]);

  return null;
}

/** JSON-LD for the homepage (Organization schema) */
export function homepageJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CIEDA",
    legalName: "Compagnie Import Export & Distribution Africaine",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.png`,
    description: DEFAULT_DESCRIPTION,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Hann Mariste II",
      addressLocality: "Dakar",
      addressCountry: "SN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+221-76-604-22-74",
      contactType: "customer service",
      availableLanguage: ["French", "Wolof"],
    },
    brand: [
      { "@type": "Brand", name: "Countre Dairy" },
      { "@type": "Brand", name: "Yousra" },
      { "@type": "Brand", name: "Florida" },
      { "@type": "Brand", name: "KamaX" },
    ],
  };
}

/** JSON-LD for a product */
export function productJsonLd(product: {
  name: string;
  description?: string;
  price_fcfa: number;
  image_url?: string;
  brand?: string;
  in_stock?: boolean;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description ?? product.name,
    image: product.image_url,
    brand: product.brand ? { "@type": "Brand", name: product.brand } : undefined,
    offers: {
      "@type": "Offer",
      price: product.price_fcfa,
      priceCurrency: "XOF",
      availability: product.in_stock !== false
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: {
        "@type": "Organization",
        name: "CIEDA",
      },
    },
  };
}

/** JSON-LD BreadcrumbList */
export function breadcrumbJsonLd(
  items: Array<{ name: string; path: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}
